import { useCallback, useEffect, useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

import type { DecisionMakerMatch } from '#src/app/pages/search/shared/api';
import { useSearchAssetUrl, useSearchResults, useSearchState } from '#src/app/pages/search/shared/hooks';
import { useTrackOnce } from '#src/app/shared/hooks/tracking';

type DecisionMakerCardClickTarget = 'name' | 'view_profile';

type DecisionMakerCardData = {
	name: string;
	title: string | undefined;
	avatarUrl: string | undefined;
	profileUrl: string;
	verified: boolean;
	petitionsCount: number;
	petitionsCountLocalized: string;
};

type Result = ModelHookResult<
	{ decisionMaker: DecisionMakerCardData | null },
	{ trackClick: (target: DecisionMakerCardClickTarget) => void }
>;

type CardTrackingData = Required<
	Pick<
		TrackingProperties,
		'dm_id' | 'dm_verified' | 'dm_petitions_num' | 'q' | 'total_count' | 'match_type' | 'card_variant'
	>
>;

const CARD_VARIANT = 'profile_card';

function normalizeQuery(query: string): string {
	return query.trim().toLowerCase();
}

export function useDecisionMakerCard(): Result {
	const { decisionMaker: match, nbHits, query, isLoading, isError } = useSearchResults();
	const { query: currentQuery, page } = useSearchState();
	const { localizeNumber } = useI18n();
	const getAssetUrl = useSearchAssetUrl();
	const track = useTracking();

	// while a new query or page loads, the previous results (and their match) are still around: only show a
	// match that belongs to what's on screen now, so the card doesn't linger above another query's results
	const visibleMatch: DecisionMakerMatch | null =
		match && !isError && page === 0 && normalizeQuery(query) === normalizeQuery(currentQuery) ? match : null;

	// one view per decision maker per query, even as other filters change the results
	const trackViewOnce = useTrackOnce([visibleMatch?.decisionMaker.id, query]);

	const trackingData = useMemo(
		(): CardTrackingData | null =>
			visibleMatch && {
				dm_id: `dm_${visibleMatch.decisionMaker.id}`,
				dm_verified: !!visibleMatch.decisionMaker.verified_at,
				dm_petitions_num: visibleMatch.decisionMaker.petition_count,
				q: query,
				total_count: nbHits,
				match_type: visibleMatch.matchType,
				card_variant: CARD_VARIANT,
			},
		[nbHits, query, visibleMatch],
	);

	useEffect(() => {
		// while fetching, the previous results are still shown
		if (!trackingData || isLoading) return;
		void trackViewOnce('search_dm_card_view', trackingData);
	}, [isLoading, trackViewOnce, trackingData]);

	const trackClick = useCallback(
		(target: DecisionMakerCardClickTarget) => {
			if (!trackingData) return;
			void track('search_dm_card_click', { ...trackingData, target });
		},
		[track, trackingData],
	);

	return {
		data: {
			decisionMaker: useMemo(() => {
				if (!visibleMatch) return null;
				const { decisionMaker } = visibleMatch;
				return {
					name: decisionMaker.name,
					title: decisionMaker.title,
					avatarUrl: getAssetUrl(decisionMaker.photo_url),
					profileUrl: `/decision-makers/${encodeURIComponent(decisionMaker.slug)}?source_location=search_dm_card`,
					verified: !!decisionMaker.verified_at,
					petitionsCount: decisionMaker.petition_count,
					petitionsCountLocalized: localizeNumber(decisionMaker.petition_count),
				};
			}, [getAssetUrl, localizeNumber, visibleMatch]),
		},
		actions: { trackClick },
	};
}
